import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProblemTextPanel from './ProblemTextPanel';
import InteractiveGraph from './InteractiveGraph';
import ChatInterface from './ChatInterface';
import FuriganaText from './FuriganaText';
import AttentionGuide from './AttentionGuide';
import { researchAPI, ttsAPI } from '../utils/api';
import {
  LG_BREAKPOINT,
  NARROW_GRAPH_FLOOR,
  NARROW_SIDE_FLOOR,
  OUTER_CHROME,
  TWO_COLUMN_MIN_ROW_WIDTH,
  TWO_COLUMN_MIN_ROW_WIDTH_LG,
  WIDE_FLOORS_MIN_ROW_WIDTH,
} from '../utils/tabletLayoutThresholds';
import { useActionLogger } from '../hooks/useActionLogger';
import { useResearchFeatures } from '../hooks/useResearchFeatures';
import { hoverSamplerForTask } from '../utils/hoverSampler';
import { describeEscalationCause } from '../utils/escalationAttribution';
import BurdenProbe from './BurdenProbe';
import { resolveActionLoggerUserId } from '../utils/actionLoggerIdentity';
import {
  EVENT_SOURCES,
  buildReplayIntervalMs,
  computeEffectiveSupportLevel,
  computeStableOverlays,
  sanitizeUiActions,
  splitReplayActions,
} from '../utils/supportLevelPolicy';
import { logClientWarn } from '../utils/safeLog';

const createReplayId = () => `replay-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
// Phase A task-event idempotency key (server pattern ^[A-Za-z0-9_-]{8,64}$).
const EMPTY_ACTIONS = [];
// For resets of task-scoped state: keeps an entry of the current task, drops older ones.
const dropStaleEntry = (generation) => (prev) => (
  prev && prev.generation !== generation ? null : prev
);
const createClientEventId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `ev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const createAudioUrlFromBase64 = (base64, mimeType = 'audio/mp3') => {
  if (!base64) return '';
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
};

function ProblemIntegrationView({
  problemId,
  sessionId,
  graphId,
  graphData,
  problemData,
  studentProfile,
  settings,
  onExit,
  // Phase A: the server's per-task record (present only when the instrumentation flag is on).
  researchTask = null,
}) {
  const { t } = useTranslation();
  // Phase A research flags (all OFF by default and for teachers; server-enforced).
  const researchUserKey = resolveActionLoggerUserId(studentProfile);
  const researchFeatures = useResearchFeatures(researchUserKey);
  const instrumented = researchFeatures.instrumentation === true;
  const activeResearchTask = instrumented ? researchTask : null;
  // ① / ② / ③ are interventions: they run only while micro-randomization (randomized tasks)
  // or the end-of-session practice (practice tasks, every support on) is on for this student
  // NOW. The server masks each task's values to what is in force for its kind, so a practice
  // flag never turns on a randomized task's factors and vice versa.
  // Each task is governed by its own flag (Codex practice-R1): a practice task by the practice
  // flag, every other task by the randomization flag — so a randomized task never runs under
  // the practice flag even if the features response changes after the task loaded.
  const randomizationOn = instrumented && (
    activeResearchTask?.task_kind === 'practice'
      ? researchFeatures.practice_mode === true
      : researchFeatures.micro_randomization === true
  );
  const { logAction, logEscalation } = useActionLogger({
    sessionId: sessionId,
    // F1: the durable action-log outbox is namespaced by an authenticated user id. The real
    // studentProfile shape (Dashboard.js) exposes it as `student_id` (no `user_id`), so read
    // via the resolver or the outbox never engages for actual students.
    userId: researchUserKey,
    stampClientTime: instrumented,
    problemId,
  });

  const containerRef = useRef(null);
  const timersRef = useRef({ graph: null, text: null, tts: null });
  const audioRef = useRef(null);
  const prevEffectiveLevelRef = useRef(null);
  const layoutInfoRef = useRef({ containerWidth: 0 });
  const separatorRef = useRef(null);
  const replayTimerRef = useRef(null);
  const pageVisibilityReplayIdRef = useRef(null);

  // Phase 2: recent interaction events for cognitive adaptation
  const recentEventsRef = useRef([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const reClickMapRef = useRef(new Map()); // {elementId: {count, firstClickTime}}
  const idleTimerRef = useRef(null);
  const lastInteractionRef = useRef(Date.now());
  const recentMessagesRef = useRef([]); // last 3 message texts
  const currentStageRef = useRef(null);
  // Timestamp of the last programmatic __trigger_last_stage__ jump request.
  // Time-window based (not consume-once) because InteractiveGraph skips
  // onStageChange entirely when already at the final stage, which would leave
  // a boolean flag dangling and wrongly suppress the next manual stage change.
  const programmaticStageJumpAtRef = useRef(0);

  const [activeQuestionEntry, setActiveQuestionEntry] = useState(null);
  const [highlightedGraphElements, setHighlightedGraphElements] = useState([]);
  const [highlightedTextKeywords, setHighlightedTextKeywords] = useState([]);
  const [oneShotAttentionGuide, setOneShotAttentionGuide] = useState(null);
  const [supportLevel, setSupportLevel] = useState(2);
  const [focusEntry, setFocusEntry] = useState(null);
  const [transientUiActions, setTransientUiActions] = useState([]);
  // Phase A ② (micro-randomized): the "initial display" layer — highlight the task's
  // relevant elements and dim the rest when a task/question starts. It stays until the
  // student's first interaction (unlike transient linkage, which clears in ~3 s).
  const [initialSuppressionEntry, setInitialSuppressionEntry] = useState(null);
  // Phase A ③ (micro-randomized): the link invitation, shown once per task open.
  const [linkInvitationGeneration, setLinkInvitationGeneration] = useState(null);
  // Phase A burden probe: shown after an answer when the server says it is due.
  const [burdenProbeFor, setBurdenProbeFor] = useState(null);
  const [replayState, setReplayState] = useState(null);

  // ---- Task identity (Codex B-R4) ----
  // Every task-scoped value (focus jump, ② layer, ③ invitation, burden prompt, render and
  // navigation stage) is tagged with the task generation it was written for, and masked at
  // render time when it belongs to an earlier task. The generation is bumped DURING RENDER,
  // before any child sees the new task: resetting in an effect is too late, because the
  // children's effects (the chart's first render of the new task) run before the parent's.
  // Resets in effects only drop stale entries, so they can never wipe a value the new task's
  // children already reported in the same commit.
  //
  // The generation is React STATE, advanced with the "adjust state when a prop changes"
  // render-phase update: a render React abandons (Suspense, transition) rolls it back with
  // the render, so the task still on screen keeps its generation (Codex B-R5). Setters tag
  // with the generation of the render that created them; async callbacks compare with
  // taskGenerationRef, which holds the COMMITTED generation (synced in a layout effect).
  const [taskIdentity, setTaskIdentity] = useState(() => ({ problemId, generation: 0 }));
  let currentTaskGeneration = taskIdentity.generation;
  if (taskIdentity.problemId !== problemId) {
    currentTaskGeneration = taskIdentity.generation + 1;
    setTaskIdentity({ problemId, generation: currentTaskGeneration });
  }
  const taskGenerationRef = useRef(currentTaskGeneration);
  useLayoutEffect(() => {
    taskGenerationRef.current = currentTaskGeneration;
  }, [currentTaskGeneration]);
  const isThisTask = (entry) => Boolean(entry) && entry.generation === currentTaskGeneration;
  const setFocusElementId = useCallback((id) => {
    setFocusEntry(id == null ? null : { id, generation: currentTaskGeneration });
  }, [currentTaskGeneration]);
  const focusElementId = isThisTask(focusEntry) ? focusEntry.id : null;
  const setInitialSuppressionActions = useCallback((actions) => {
    setInitialSuppressionEntry(Array.isArray(actions) && actions.length
      ? { actions, generation: currentTaskGeneration }
      : null);
  }, [currentTaskGeneration]);
  const initialSuppressionActions = isThisTask(initialSuppressionEntry)
    ? initialSuppressionEntry.actions
    : EMPTY_ACTIONS;
  const setLinkInvitationVisible = useCallback((visible) => {
    setLinkInvitationGeneration(visible ? { generation: currentTaskGeneration } : null);
  }, [currentTaskGeneration]);
  const linkInvitationVisible = isThisTask(linkInvitationGeneration);
  // The selected question belongs to its task too: a q2 selected in task A must not be
  // the context of task B's first render or of its task-open events (Codex B-R5).
  const setActiveQuestion = useCallback((question) => {
    setActiveQuestionEntry(question ? { question, generation: currentTaskGeneration } : null);
  }, [currentTaskGeneration]);
  const activeQuestion = isThisTask(activeQuestionEntry) ? activeQuestionEntry.question : null;
  const activeBurdenProbe = isThisTask(burdenProbeFor) ? burdenProbeFor : null;
  const burdenPromptSeqRef = useRef(0);
  // Answers whose save is not confirmed yet, one per question of the current task (their event
  // id is reused on a retry; another question's answer in between must not replace it —
  // Codex B-R11). Keyed by question id; entries of an earlier task are ignored.
  const pendingAnswerRef = useRef(new Map());
  const burdenCloseTimerRef = useRef(null);
  // Committed values for async callbacks (a save response that returns after a switch).
  const activeBurdenProbeRef = useRef(activeBurdenProbe);
  useLayoutEffect(() => {
    activeBurdenProbeRef.current = activeBurdenProbe;
  });
  const viewMountedRef = useRef(true);
  useEffect(() => {
    viewMountedRef.current = true;
    return () => { viewMountedRef.current = false; };
  }, []);

  // --- Cognitive overlay state ---
  // initial_overlays come from the upload API (Fixer 1) or are computed client-side as fallback
  const stableOverlays = useMemo(() => {
    if (Array.isArray(graphData?.initial_overlays) && graphData.initial_overlays.length > 0) {
      return graphData.initial_overlays;
    }
    // Fallback: compute from cognitive profile on the client. Ground show_value_tags
    // with the chart's element ids so it actually renders (A' fix 2026-06-15).
    const fallbackElements = graphData?.elements || graphData?.graph_data?.elements || [];
    const fallbackElementIds = Array.isArray(fallbackElements)
      ? fallbackElements.filter((e) => e && e.id).map((e) => e.id)
      : [];
    return computeStableOverlays(studentProfile?.cognitive_difficulty, fallbackElementIds);
  }, [graphData?.initial_overlays, graphData?.elements, graphData?.graph_data?.elements, studentProfile?.cognitive_difficulty]);

  // Phase 2: stage change handler moved below (handleStageChange)

  const defaultGraphRatio = 0.62;

  // The width the two panels actually get to share. Measured on the row
  // itself: as a stretched child of a column container its width is set by
  // Dashboard's layout, NOT by its own children, so reading it back does not
  // feed on the decision it drives (its scrollWidth would; its box width does
  // not). This replaced a viewport-based cutover once the inline lesson-list
  // sidebar started appearing on iPad landscape — the same viewport width then
  // leaves the row ~300px narrower, and a viewport constant cannot know that.
  // Until the first ResizeObserver callback, estimate from the viewport minus
  // the compact-mode chrome so the first paint is right on tablets.
  const rowRef = useRef(null);
  const [rowWidth, setRowWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth - OUTER_CHROME : 1280
  ));
  // The separator is `hidden lg:flex`: from lg the row carries it and a
  // second gap, so the minimum it must hold is larger. Same breakpoint as
  // the class, kept in one constant.
  const [separatorShown, setSeparatorShown] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth >= LG_BREAKPOINT : true
  ));
  const twoColumn = rowWidth >= (separatorShown ? TWO_COLUMN_MIN_ROW_WIDTH_LG : TWO_COLUMN_MIN_ROW_WIDTH);
  const isNarrowTwoColumn = rowWidth < WIDE_FLOORS_MIN_ROW_WIDTH;
  const minGraphWidth = isNarrowTwoColumn ? NARROW_GRAPH_FLOOR : 520;
  const minSideWidth = isNarrowTwoColumn ? NARROW_SIDE_FLOOR : 360;
  const [graphPanelWidth, setGraphPanelWidth] = useState(720);
  // Part 2 PR1: the renderer's report of what is on screen, lifted here so the
  // chat panel can send it without recomputing visibility itself.
  const [renderEntry, setRenderEntry] = useState(null);
  const handleRenderStateChange = useCallback((state) => {
    setRenderEntry(state == null ? null : { state, generation: currentTaskGeneration });
  }, [currentTaskGeneration]);
  const renderState = isThisTask(renderEntry) ? renderEntry.state : null;
  // The stage actually on screen, for task events (Codex B-R3): the chart restores
  // its stage from graphData without calling onStageChange, so the last navigation
  // event alone is not the current stage.
  const renderStateRef = useRef(null);
  useLayoutEffect(() => {
    renderStateRef.current = renderState;
  });
  // The last navigation event, only if it belongs to this task.
  const navigatedStage = useCallback(() => {
    const entry = currentStageRef.current;
    return entry && entry.generation === taskGenerationRef.current ? entry.stage : null;
  }, []);
  const currentStageForEvents = useCallback(() => (
    Number.isInteger(renderStateRef.current?.stage_index)
      ? renderStateRef.current.stage_index
      : navigatedStage()
  ), [navigatedStage]);
  const graphWidthRef = useRef(graphPanelWidth);

  useEffect(() => {
    const node = rowRef.current;
    if (!node || typeof window === 'undefined') return undefined;
    const read = () => {
      setRowWidth(node.getBoundingClientRect().width);
      setSeparatorShown(window.innerWidth >= LG_BREAKPOINT);
    };
    read();
    if (typeof ResizeObserver === 'undefined') {
      // Very old engines: fall back to viewport events, still reading the row.
      window.addEventListener('resize', read);
      window.addEventListener('orientationchange', read);
      return () => {
        window.removeEventListener('resize', read);
        window.removeEventListener('orientationchange', read);
      };
    }
    const ro = new ResizeObserver(read);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const integrationHints = useMemo(
    () => (Array.isArray(problemData?.integration_hints) ? problemData.integration_hints : []),
    [problemData]
  );
  const questionsById = useMemo(() => {
    const items = Array.isArray(problemData?.questions) ? problemData.questions : [];
    const map = new Map();
    items.forEach((item) => {
      if (item?.id) {
        map.set(item.id, item);
      }
    });
    return map;
  }, [problemData]);
  const resolvedGraphData = useMemo(() => {
    if (!graphData) return graphData;
    if (graphData.graph_id || !graphId) return graphData;
    return { ...graphData, graph_id: graphId };
  }, [graphData, graphId]);
  const resolvedGraphType = useMemo(
    () => (
      resolvedGraphData?.graph_type ||
      resolvedGraphData?.graph_data?.graph_type ||
      graphData?.graph_type ||
      graphData?.graph_data?.graph_type ||
      ''
    ),
    [graphData?.graph_data?.graph_type, graphData?.graph_type, resolvedGraphData?.graph_data?.graph_type, resolvedGraphData?.graph_type]
  );
  const graphElements = useMemo(() => {
    // Try multiple paths to find elements array
    const candidates = [
      resolvedGraphData?.graph_data?.elements,
      resolvedGraphData?.elements,
      resolvedGraphData?.data?.elements,
      resolvedGraphData?.data?.graph_data?.elements,
    ];
    for (const elements of candidates) {
      if (Array.isArray(elements) && elements.length > 0) {
        return elements;
      }
    }
    return [];
  }, [resolvedGraphData]);
  const {
    level: effectiveSupportLevel,
    triggerReason: escalationTriggerReason,
    triggerEvent: escalationTriggerEvent,
    ruleId: escalationRuleId,
    floorRuleId: escalationFloorRuleId,
  } = useMemo(
    () => computeEffectiveSupportLevel({
      baseSupportLevel: supportLevel,
      cognitiveDifficulty: studentProfile?.cognitive_difficulty,
      activeQuestion,
      recentEvents,
      graphType: resolvedGraphType,
      hasProblemTextContext: Boolean(problemData?.stem || problemData?.questions?.length),
    }),
    [activeQuestion, problemData?.questions?.length, problemData?.stem, recentEvents, resolvedGraphType, studentProfile?.cognitive_difficulty, supportLevel]
  );
  const stableUiActions = useMemo(
    () => sanitizeUiActions(stableOverlays, {
      graphType: resolvedGraphType,
      supportLevel: effectiveSupportLevel,
      stableLayer: true,
    }),
    [effectiveSupportLevel, resolvedGraphType, stableOverlays]
  );
  const mergedUiActions = useMemo(
    () => [...stableUiActions, ...initialSuppressionActions, ...transientUiActions],
    [stableUiActions, initialSuppressionActions, transientUiActions]
  );

  // ---- Phase A research instrumentation (inert unless the server flag is on) ----
  // Context read at call time through refs, so the logging callbacks stay stable and
  // never become new render dependencies of the chart.
  // Synced after commit (not during render), so a render React abandons never leaks into
  // what the callbacks log (Codex B-R5).
  const instrumentedRef = useRef(instrumented);
  const researchCtxRef = useRef({});
  const activeQuestionId = activeQuestion?.id ?? null;
  useLayoutEffect(() => {
    instrumentedRef.current = instrumented;
    researchCtxRef.current = {
      questionId: activeQuestionId,
      supportLevel,
      effectiveSupportLevel,
      graphType: resolvedGraphType || null,
    };
  });
  // Never carries content text: only types, element ids and small numeric metadata.
  const logResearchAction = useCallback((actionType, { elementIds = null, eventSource = 'other', metadata = null, questionId } = {}) => {
    if (!instrumentedRef.current) return;
    const ctx = researchCtxRef.current;
    logAction({
      // An explicit question id (even null) wins: at question selection the state that
      // feeds ctx has not updated yet.
      question_id: questionId !== undefined ? questionId : ctx.questionId,
      action_type: actionType,
      element_ids: elementIds,
      event_source: eventSource,
      base_support_level: ctx.supportLevel,
      effective_support_level: ctx.effectiveSupportLevel,
      graph_type: ctx.graphType,
      metadata,
    });
  }, [logAction]);
  // re_click / idle / re_question were detected but only ever sent inside chat
  // requests; persist them as their own rows (re_question WITHOUT its words).
  const logBehaviourEventRef = useRef(null);
  const logBehaviourEvent = (event) => {
    if (!event?.type) return;
    if (event.type === 're_click') {
      logResearchAction('re_click', {
        elementIds: event.element_id ? [event.element_id] : null,
        eventSource: 'click_event',
        metadata: { count: event.count ?? null, interval_sec: event.interval_sec ?? null },
      });
    } else if (event.type === 'idle') {
      logResearchAction('idle', { metadata: { duration_sec: event.duration_sec ?? null, stage: event.stage ?? null } });
    } else if (event.type === 're_question') {
      logResearchAction('re_question', { metadata: { count: event.count ?? null } });
    }
  };
  // One sampler per (student, task), held at module level (utils/hoverSampler.js): this view is
  // unmounted while the dashboard loads another lesson, and the limits must hold across
  // leaving and returning (supervisor review 2026-10-08).
  const hoverSamplerFor = useCallback(
    (taskId) => hoverSamplerForTask(`${researchUserKey || ''}::${taskId || ''}`),
    [researchUserKey],
  );
  // Bound to the render that created it (the chart captures it at pointerenter): a hover that
  // ends after a task switch is dropped before it is sampled, and a recorded one keeps the
  // question that was current when it began (Codex B-R10).
  const handleElementHoverDwell = useCallback((elementId, dwellMs) => {
    if (currentTaskGeneration !== taskGenerationRef.current) return;
    const sample = hoverSamplerFor(problemId).offer(elementId, dwellMs);
    if (!sample) return;
    logResearchAction('element_hover', {
      elementIds: [elementId], eventSource: 'hover_event', metadata: sample, questionId: activeQuestionId,
    });
  }, [activeQuestionId, currentTaskGeneration, hoverSamplerFor, logResearchAction, problemId]);
  // Bound to the render that created it: the chart / chat capture this callback when a
  // speech request starts and report its success / error through the same one, so the events
  // keep that task's question, and a result arriving after a task switch is dropped
  // (Codex B-R7).
  const handleTtsEvent = useCallback((kind, trigger) => {
    if (kind !== 'tts_start' && kind !== 'tts_success' && kind !== 'tts_error') return;
    if (currentTaskGeneration !== taskGenerationRef.current) return;
    logResearchAction(kind, { questionId: activeQuestionId, metadata: { trigger: trigger || 'other' } });
  }, [activeQuestionId, currentTaskGeneration, logResearchAction]);

  // Task events (question start / answer / burden) go to their own endpoint. A few
  // retries on network or 5xx errors; a 4xx means the server refused it, so stop.
  const sendTaskEvent = useCallback(async (event) => {
    if (!instrumentedRef.current || !problemId || !sessionId) return null;
    // One id per logical event, reused by every retry: if the server committed but the
    // response was lost, the retry is deduplicated instead of becoming a second
    // attempt / visit (Codex R1 P2).
    // A caller may pass its own id so that a retry by the student (burden item after a
    // failed save) is the same logical event (Codex B-R4).
    const clientEventId = event.client_event_id || createClientEventId();
    const clientEventAtMs = Date.now();
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await researchAPI.postTaskEvent({
          session_id: sessionId,
          problem_id: problemId,
          client_event_id: clientEventId,
          client_event_at_ms: clientEventAtMs,
          ...event,
        });
        return response?.data || null;
      } catch (err) {
        const status = err?.response?.status;
        if (status && status < 500) return null;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
    return null;
  }, [problemId, sessionId]);

  // Track effective support level changes for escalation logging
  const manualLevelChangeRef = useRef(false);
  const dismissInitialSuppressionRef = useRef(null);
  // resolveGraphElementTargets is defined further down; ② reads it at call time.
  const resolveTargetsRef = useRef(null);
  useEffect(() => {
    if (effectiveSupportLevel == null) return;
    if (prevEffectiveLevelRef.current === null) {
      prevEffectiveLevelRef.current = effectiveSupportLevel;
      return;
    }
    if (prevEffectiveLevelRef.current !== effectiveSupportLevel) {
      const record = {
        question_id: activeQuestion?.id || null,
        previous_effective_level: prevEffectiveLevelRef.current,
        new_effective_level: effectiveSupportLevel,
        base_support_level: supportLevel,
        trigger_reason: escalationTriggerReason,
        graph_type: resolvedGraphType || null,
      };
      if (instrumentedRef.current) {
        // Phase A (rev3 M4): the condition, the axis and the rule apart, and
        // whether the student changed the level themselves.
        Object.assign(record, describeEscalationCause({
          manual: manualLevelChangeRef.current,
          previousLevel: prevEffectiveLevelRef.current,
          newLevel: effectiveSupportLevel,
          triggerReason: escalationTriggerReason,
          triggerEvent: escalationTriggerEvent,
          ruleId: escalationRuleId,
          floorRuleId: escalationFloorRuleId,
        }));
      }
      manualLevelChangeRef.current = false;
      logEscalation(record);
      prevEffectiveLevelRef.current = effectiveSupportLevel;
    }
  }, [effectiveSupportLevel, escalationTriggerReason, escalationTriggerEvent, escalationRuleId, escalationFloorRuleId, logEscalation, activeQuestion, supportLevel, resolvedGraphType]);

  // The level selector. With instrumentation on, a manual change is always
  // recorded — also when a floor keeps the effective level where it was, which
  // the effect above cannot see.
  const handleSupportLevelChange = useCallback((nextLevel) => {
    // A manual level choice is a student action: the ② initial layer gives way
    // (it was sanitized for the previous level; Lv1 must not keep a dim).
    dismissInitialSuppressionRef.current?.();
    if (instrumentedRef.current && nextLevel !== supportLevel) {
      const nextEffective = computeEffectiveSupportLevel({
        baseSupportLevel: nextLevel,
        cognitiveDifficulty: studentProfile?.cognitive_difficulty,
        activeQuestion,
        recentEvents,
        graphType: resolvedGraphType,
        hasProblemTextContext: Boolean(problemData?.stem || problemData?.questions?.length),
      }).level;
      if (nextEffective === effectiveSupportLevel) {
        logEscalation({
          question_id: activeQuestion?.id || null,
          previous_effective_level: effectiveSupportLevel,
          new_effective_level: effectiveSupportLevel,
          base_support_level: nextLevel,
          trigger_reason: null,
          graph_type: resolvedGraphType || null,
          ...describeEscalationCause({ manual: true }),
        });
      } else {
        manualLevelChangeRef.current = true;
      }
    }
    setSupportLevel(nextLevel);
  }, [activeQuestion, effectiveSupportLevel, logEscalation, problemData?.questions?.length, problemData?.stem, recentEvents, resolvedGraphType, studentProfile?.cognitive_difficulty, supportLevel]);

  // ---- Phase A ②: initial suppression/highlight display (micro-randomized) ----
  // Set by any student action on this task; the task-open display is never applied
  // after it (a late feature-flag response must not re-dim a chart the student has
  // already worked with — Codex R1 P2).
  const studentActedRef = useRef(false);
  const dismissInitialSuppression = useCallback(() => {
    studentActedRef.current = true;
    setInitialSuppressionEntry((prev) => (prev ? null : prev));
  }, []);
  // Relevant elements: the task catalog's frozen set when the server resolved one,
  // else the question's integration hints (the same source as Question.graph_focus),
  // else the union over all questions at task open.
  const resolveRelevantElements = useCallback((question) => {
    const known = new Set(graphElements.map((elem) => elem?.id).filter(Boolean));
    // Catalog and hint targets may be virtual ids (x_axis_label_*, data_point_*);
    // resolve them exactly as keyword linkage does instead of dropping them.
    const toRenderable = (rawIds) => {
      const out = [];
      const push = (id) => { if (id && !out.includes(id)) out.push(id); };
      (rawIds || []).forEach((id) => {
        if (!id) return;
        if (known.has(id)) { push(id); return; }
        const resolved = resolveTargetsRef.current ? resolveTargetsRef.current(id, null) : null;
        (resolved?.highlightIds || []).forEach(push);
      });
      return out;
    };
    const fromCatalog = toRenderable(
      Array.isArray(activeResearchTask?.relevant_element_ids) ? activeResearchTask.relevant_element_ids : []
    );
    if (fromCatalog.length) return { ids: fromCatalog, source: 'catalog' };
    const hints = question
      ? integrationHints.filter((hint) => hint?.question_id === question.id)
      : integrationHints;
    const ids = toRenderable(hints.map((hint) => hint?.graph_element_id));
    return { ids, source: ids.length ? 'graph_focus' : 'none' };
  }, [activeResearchTask, graphElements, integrationHints]);
  // trigger: 'task_open' (the task was opened) or 'question_start' (a question's first
  // presentation). Both are applications of the same assignment; the trigger and the
  // question id let the analysis tell them apart (browser check 2026-10-08).
  const applyInitialSuppression = useCallback((question, trigger) => {
    if (!randomizationOn || !activeResearchTask?.m2_initial_suppression) return;
    const { ids, source } = resolveRelevantElements(question);
    const questionId = question?.id || null;
    if (!ids.length) {
      setInitialSuppressionActions([]);
      logResearchAction('initial_suppression_applied', {
        questionId,
        metadata: { applied_types: [], target_source: 'none', trigger },
      });
      return;
    }
    const dimIds = graphElements.map((elem) => elem?.id).filter((id) => id && !ids.includes(id));
    const raw = [{ type: 'highlight_elements', element_ids: ids }];
    if (dimIds.length) raw.push({ type: 'dim_irrelevant', element_ids: dimIds });
    // Same level rules as every other overlay (Lv1 does not dim).
    const sanitized = sanitizeUiActions(raw, {
      graphType: resolvedGraphType,
      supportLevel: effectiveSupportLevel,
      eventSource: EVENT_SOURCES.QA,
      currentQuestionId: question?.id || null,
      currentQuestionText: question?.text || '',
    });
    setInitialSuppressionActions(sanitized);
    logResearchAction('initial_suppression_applied', {
      elementIds: ids,
      questionId,
      metadata: { applied_types: sanitized.map((action) => action.type), target_source: source, trigger },
    });
  }, [activeResearchTask, effectiveSupportLevel, graphElements, logResearchAction, randomizationOn, resolveRelevantElements, resolvedGraphType, setInitialSuppressionActions]);

  // Task open: ② initial display and ③ invitation, once per task, by its assignment.
  const taskOpenedRef = useRef(null);
  const fullViewJumpRef = useRef({ raf: null, timeout: null });
  // A new task in the same mounted view (Dashboard switches lessons without a key):
  // nothing task-level may carry over — the ② layer, the ③ invitation, the burden
  // prompt, presentation times keyed by local q1/q2, the hover budget (Codex R1 P2).
  const previousProblemIdRef = useRef(problemId);
  // taskGenerationRef (declared above, bumped during render) is what async responses
  // compare, so a reply that arrives after the switch cannot act on the new task (Codex B-R2).
  useEffect(() => {
    if (previousProblemIdRef.current === problemId) return;
    previousProblemIdRef.current = problemId;
    taskOpenedRef.current = null;
    if (fullViewJumpRef.current.raf != null) cancelAnimationFrame(fullViewJumpRef.current.raf);
    if (fullViewJumpRef.current.timeout != null) clearTimeout(fullViewJumpRef.current.timeout);
    fullViewJumpRef.current = { raf: null, timeout: null };
    // Drop only entries of earlier tasks (the new task's children may already have
    // reported in this same commit — Codex B-R4).
    setFocusEntry(dropStaleEntry(currentTaskGeneration));
    setRenderEntry(dropStaleEntry(currentTaskGeneration));
    if (burdenCloseTimerRef.current != null) {
      clearTimeout(burdenCloseTimerRef.current);
      burdenCloseTimerRef.current = null;
    }
    studentActedRef.current = false;
    questionFirstStartRef.current = new Map();
    questionStartSentRef.current = new Set();
    pendingAnswerRef.current = new Map();
    setInitialSuppressionEntry(dropStaleEntry(currentTaskGeneration));
    setLinkInvitationGeneration(dropStaleEntry(currentTaskGeneration));
    setBurdenProbeFor(dropStaleEntry(currentTaskGeneration));
    setActiveQuestionEntry(dropStaleEntry(currentTaskGeneration));
  }, [problemId, currentTaskGeneration]);
  useEffect(() => {
    const taskKey = activeResearchTask?.task_instance_id;
    if (!randomizationOn || !taskKey || taskOpenedRef.current === taskKey || graphElements.length === 0) return;
    taskOpenedRef.current = taskKey;
    // Never after the student has acted on this task (late flag response).
    if (!studentActedRef.current) {
      // ① 段階提示 OFF (rev3 M1): open the task on the complete chart (final
      // stage). Same programmatic jump keyword linkage uses; the student can step
      // back through the stages at any time. Marked programmatic so the stage
      // change does not count as the student's first action.
      if (activeResearchTask.m1_full_view_initial === true) {
        const generation = taskGenerationRef.current;
        programmaticStageJumpAtRef.current = Date.now();
        setFocusElementId(null);
        // Bound to THIS task: cancelled on a task switch, and re-checked before it
        // fires, so a task assigned m1 = False never receives the jump (Codex B-R3).
        fullViewJumpRef.current.raf = requestAnimationFrame(() => {
          fullViewJumpRef.current.raf = null;
          if (generation !== taskGenerationRef.current || studentActedRef.current) return;
          setFocusElementId('__trigger_last_stage__');
          fullViewJumpRef.current.timeout = setTimeout(() => {
            fullViewJumpRef.current.timeout = null;
            if (generation !== taskGenerationRef.current) return;
            setFocusElementId(null);
          }, 200);
        });
      }
      applyInitialSuppression(null, 'task_open');
    }
    if (activeResearchTask.m3_link_invitation) {
      setLinkInvitationVisible(true);
      logResearchAction('link_invitation_shown');
    }
  }, [activeResearchTask, applyInitialSuppression, graphElements.length, logResearchAction, randomizationOn, setFocusElementId, setLinkInvitationVisible]);
  const handleLinkInvitationDismiss = useCallback(() => {
    // A student click: the ② initial layer gives way too.
    dismissInitialSuppression();
    setLinkInvitationVisible(false);
    // A student click: logged as click_event so the server derives origin=student.
    logResearchAction('link_invitation_dismissed', { eventSource: 'click_event' });
  }, [dismissInitialSuppression, logResearchAction, setLinkInvitationVisible]);

  // Question presentation (課題単位の開始). The first start of a question is its
  // presentation; a later start is a revisit (設問パネル再訪). Latency is measured
  // from the first start with the monotonic clock.
  const questionFirstStartRef = useRef(new Map());
  // Questions whose question_start was actually sent (flags may load after the
  // student already selected one — see the OFF→ON effect below).
  const questionStartSentRef = useRef(new Set());
  const sendQuestionStart = (question) => {
    const ctx = researchCtxRef.current;
    questionStartSentRef.current.add(question.id);
    sendTaskEvent({
      event_type: 'question_start',
      question_local_id: question.id,
      stage: currentStageForEvents(),
      base_support_level: ctx.supportLevel,
      effective_support_level: ctx.effectiveSupportLevel,
    });
  };
  const questionStartRef = useRef(null);
  const questionStart = (question) => {
    if (!question?.id) return;
    const seen = questionFirstStartRef.current.has(question.id);
    if (!seen) questionFirstStartRef.current.set(question.id, performance.now());
    if (!instrumentedRef.current) return;
    sendQuestionStart(question);
    if (seen) logResearchAction('problem_panel_revisit', { metadata: { trigger: 'reselect' } });
  };
  const sendQuestionStartRef = useRef(null);
  useEffect(() => {
    if (activeQuestion?.id) questionStartRef.current?.(activeQuestion);
  }, [activeQuestion?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Flags arrived after the student selected the current question: its presentation
  // (and the latency origin already recorded) still needs its question_start row.
  const activeQuestionRef = useRef(activeQuestion);
  useEffect(() => {
    if (!instrumented) return;
    const current = activeQuestionRef.current;
    if (current?.id && !questionStartSentRef.current.has(current.id)) {
      if (!questionFirstStartRef.current.has(current.id)) {
        questionFirstStartRef.current.set(current.id, performance.now());
      }
      sendQuestionStartRef.current?.(current);
    }
  }, [instrumented]);

  const handlePanelRevisit = useCallback((trigger) => {
    logResearchAction('problem_panel_revisit', { metadata: { trigger } });
  }, [logResearchAction]);

  const handleAnswerSubmit = useCallback(async (question, answerText) => {
    if (!question?.id) return false;
    const generation = taskGenerationRef.current;
    const submitTask = { problemId, sessionId };
    const firstAt = questionFirstStartRef.current.get(question.id);
    const ctx = researchCtxRef.current;
    // Pressing again after a failed save is the SAME answer: reuse its event id, so a save the
    // server committed but whose response was lost is not counted as a second attempt
    // (Codex B-R10). An edited answer is a new attempt with a new id.
    const pending = pendingAnswerRef.current.get(question.id);
    const clientEventId = pending && pending.generation === generation && pending.text === answerText
      ? pending.clientEventId
      : createClientEventId();
    pendingAnswerRef.current.set(question.id, { generation, text: answerText, clientEventId });
    const result = await sendTaskEvent({
      client_event_id: clientEventId,
      event_type: 'question_response',
      question_local_id: question.id,
      answer_text: answerText,
      stage: currentStageForEvents(),
      latency_ms: firstAt != null ? Math.max(0, Math.round(performance.now() - firstAt)) : null,
      base_support_level: ctx.supportLevel,
      effective_support_level: ctx.effectiveSupportLevel,
    });
    if (!result?.recorded) return false;
    if (pendingAnswerRef.current.get(question.id)?.clientEventId === clientEventId) {
      pendingAnswerRef.current.delete(question.id);
    }
    // The student may have moved to another task while this was in flight.
    if (generation !== taskGenerationRef.current) return true;
    if (result.burden_prompt_due && researchFeatures.burden_probe) {
      // Bound to the task the answer was submitted in, not to whatever is open later.
      burdenPromptSeqRef.current += 1;
      setBurdenProbeFor({
        promptId: burdenPromptSeqRef.current,
        // One id for this prompt's rating, reused if the student retries after a failed
        // save, so the server keeps exactly one rating per prompt (Codex B-R4).
        clientEventId: createClientEventId(),
        questionId: question.id,
        generation,
        ...submitTask,
      });
    }
    return true;
  }, [currentStageForEvents, problemId, researchFeatures.burden_probe, sendTaskEvent, sessionId]);

  // Resolves true only when the server recorded the rating; on false the prompt stays
  // open and offers a retry (Codex B-R4: a 429 or network failure silently lost it).
  const handleBurdenAnswer = useCallback(async (rating) => {
    const target = burdenProbeFor;
    if (!target || target.generation !== taskGenerationRef.current) return false;
    const result = await sendTaskEvent({
      client_event_id: target.clientEventId,
      event_type: 'burden_rating',
      // Explicit, so the rating can never be filed under another task.
      problem_id: target.problemId,
      session_id: target.sessionId,
      question_local_id: target.questionId,
      burden_rating: rating == null ? null : rating,
      burden_skipped: rating == null,
    });
    if (!result?.recorded) return false;
    // The response may arrive after a task switch, after a newer prompt, or after unmount:
    // then it must not touch the close timer, which now belongs to another prompt (Codex B-R5).
    if (!viewMountedRef.current
        || taskGenerationRef.current !== target.generation
        || activeBurdenProbeRef.current?.promptId !== target.promptId) {
      return true;
    }
    // Close THIS prompt after the thank-you; a later prompt (same or next task) is left alone.
    if (burdenCloseTimerRef.current != null) clearTimeout(burdenCloseTimerRef.current);
    burdenCloseTimerRef.current = setTimeout(() => {
      burdenCloseTimerRef.current = null;
      setBurdenProbeFor((prev) => (prev && prev.promptId === target.promptId ? null : prev));
    }, 1200);
    return true;
  }, [burdenProbeFor, sendTaskEvent]);

  // Auto-generate keywords from graph elements when explicit keywords are missing
  const enhancedStemKeywords = useMemo(() => {
    const explicitKeywords = problemData?.stem_keywords || [];
    if (explicitKeywords.length > 0) return explicitKeywords;

    // Extract potential keywords from graph element labels
    const stemText = (problemData?.stem || '').toLowerCase();
    if (!stemText || !graphElements.length) return [];

    const generatedKeywords = [];
    const addedTexts = new Set();

    graphElements.forEach((elem) => {
      if (!elem) return;
      const candidates = [elem.label, elem.name, elem.seriesName];
      candidates.forEach((text) => {
        if (!text || typeof text !== 'string') return;
        const trimmed = text.trim();
        if (trimmed.length < 2 || addedTexts.has(trimmed.toLowerCase())) return;
        if (stemText.includes(trimmed.toLowerCase())) {
          addedTexts.add(trimmed.toLowerCase());
          generatedKeywords.push({ text: trimmed, type: 'auto_detected' });
        }
      });
      // Also check data points
      const points = Array.isArray(elem.data_points) ? elem.data_points : [];
      points.forEach((pt) => {
        if (!pt) return;
        const ptCandidates = [pt.x_label, pt.label];
        ptCandidates.forEach((text) => {
          if (!text || typeof text !== 'string') return;
          const trimmed = text.trim();
          if (trimmed.length < 2 || addedTexts.has(trimmed.toLowerCase())) return;
          if (stemText.includes(trimmed.toLowerCase())) {
            addedTexts.add(trimmed.toLowerCase());
            generatedKeywords.push({ text: trimmed, type: 'auto_detected' });
          }
        });
      });
    });

    return generatedKeywords;
  }, [problemData?.stem, problemData?.stem_keywords, graphElements]);

  // Auto-enhance question keywords: 始终尝试从图表元素中找到匹配的关键词
  const enhancedQuestions = useMemo(() => {
    const questions = problemData?.questions || [];
    if (!graphElements.length) return questions;

    return questions.map((q) => {
      if (!q) return q;

      const questionText = (q.text || '').toLowerCase();
      if (!questionText) return q;

      // 收集所有可能的关键词（包括已有的和自动检测的）
      const existingKeywords = q.keywords || [];
      const generatedKeywords = [];
      const addedTexts = new Set();

      // 先添加已有的关键词
      existingKeywords.forEach((kw) => {
        if (kw?.text) {
          addedTexts.add(kw.text.toLowerCase());
        }
      });

      // 从图表元素中自动检测关键词
      graphElements.forEach((elem) => {
        if (!elem) return;
        const candidates = [elem.label, elem.name, elem.seriesName, elem.series_name];
        candidates.forEach((text) => {
          if (!text || typeof text !== 'string') return;
          const trimmed = text.trim();
          if (trimmed.length < 2 || addedTexts.has(trimmed.toLowerCase())) return;
          if (questionText.includes(trimmed.toLowerCase())) {
            addedTexts.add(trimmed.toLowerCase());
            generatedKeywords.push({ text: trimmed, type: 'auto_detected', graph_element_id: elem.id });
          }
        });

        // 同时检查数据点
        const points = Array.isArray(elem.data_points) ? elem.data_points : [];
        points.forEach((pt) => {
          if (!pt) return;
          const ptCandidates = [pt.x_label, pt.label, pt.category];
          ptCandidates.forEach((text) => {
            if (!text || typeof text !== 'string') return;
            const trimmed = text.trim();
            if (trimmed.length < 2 || addedTexts.has(trimmed.toLowerCase())) return;
            if (questionText.includes(trimmed.toLowerCase())) {
              addedTexts.add(trimmed.toLowerCase());
              generatedKeywords.push({ text: trimmed, type: 'auto_detected', graph_element_id: elem.id });
            }
          });
        });
      });

      // 合并已有关键词和自动检测的关键词
      const mergedKeywords = [...existingKeywords, ...generatedKeywords];
      return { ...q, keywords: mergedKeywords };
    });
  }, [problemData?.questions, graphElements]);

  const highlightDuration = settings?.highlightDuration || 3000;

  // Phase 2: helper to add a recent event
  const addRecentEvent = useCallback((event) => {
    recentEventsRef.current = [...recentEventsRef.current, { ...event, timestamp: Date.now() }];
    setRecentEvents([...recentEventsRef.current]);
    logBehaviourEventRef.current?.(event);
  }, []);

  // Phase 2: reset idle timer — call on any user interaction
  const resetIdleTimer = useCallback(() => {
    lastInteractionRef.current = Date.now();
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    // Bound to the task it was started in: a timer of an earlier task never records an idle
    // period for the next one (Codex B-R8).
    const generation = taskGenerationRef.current;
    idleTimerRef.current = setTimeout(() => {
      if (generation !== taskGenerationRef.current) return;
      const elapsed = Math.round((Date.now() - lastInteractionRef.current) / 1000);
      if (elapsed >= 45) {
        addRecentEvent({
          type: 'idle',
          duration_sec: elapsed,
          stage: navigatedStage(),
        });
      }
    }, 45 * 1000);
  }, [addRecentEvent, navigatedStage]);

  // Phase 2: track re-clicks on graph elements
  const trackReClick = useCallback((elementId) => {
    if (!elementId) return;
    lastInteractionRef.current = Date.now();
    resetIdleTimer();

    const now = Date.now();
    const entry = reClickMapRef.current.get(elementId);
    if (entry && (now - entry.firstClickTime) <= 30000) {
      entry.count += 1;
      if (entry.count >= 3) {
        addRecentEvent({
          type: 're_click',
          element_id: elementId,
          count: entry.count,
          interval_sec: Math.round((now - entry.firstClickTime) / 1000),
        });
        // Reset after recording the event
        reClickMapRef.current.delete(elementId);
      }
    } else {
      reClickMapRef.current.set(elementId, { count: 1, firstClickTime: now });
    }
  }, [addRecentEvent, resetIdleTimer]);

  // Phase 2: detect re-questions via keyword overlap
  const trackReQuestion = useCallback((messageText) => {
    if (!messageText) return;
    lastInteractionRef.current = Date.now();
    resetIdleTimer();

    const currentWords = new Set(
      messageText.toLowerCase().replace(/[。、？！,.!?]/g, ' ').split(/\s+/).filter(w => w.length >= 2)
    );
    if (currentWords.size === 0) return;

    for (const prevText of recentMessagesRef.current) {
      const prevWords = new Set(
        prevText.toLowerCase().replace(/[。、？！,.!?]/g, ' ').split(/\s+/).filter(w => w.length >= 2)
      );
      if (prevWords.size === 0) continue;
      const intersection = [...currentWords].filter(w => prevWords.has(w));
      const overlapRatio = intersection.length / Math.max(currentWords.size, 1);
      if (overlapRatio > 0.5) {
        addRecentEvent({
          type: 're_question',
          topic: intersection.slice(0, 5).join(' '),
          count: 2, // Signal to backend that threshold is met
        });
        break;
      }
    }

    // Keep last 3 messages
    recentMessagesRef.current = [...recentMessagesRef.current, messageText].slice(-3);
  }, [addRecentEvent, resetIdleTimer]);

  const clearTransientUiActions = useCallback(() => {
    setTransientUiActions([]);
    if (timersRef.current.overlay) {
      clearTimeout(timersRef.current.overlay);
      timersRef.current.overlay = null;
    }
  }, []);

  const scheduleTransientUiActionsClear = useCallback((duration = settings?.highlightDuration || 3000) => {
    if (timersRef.current.overlay) {
      clearTimeout(timersRef.current.overlay);
    }
    timersRef.current.overlay = setTimeout(() => {
      setTransientUiActions([]);
      timersRef.current.overlay = null;
    }, duration);
  }, [settings?.highlightDuration]);

  const stopReplay = useCallback((reason = 'manual_stop') => {
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
    pageVisibilityReplayIdRef.current = null;
    setReplayState(null);
    setOneShotAttentionGuide(null);
  }, []);

  const pauseReplay = useCallback((reason = 'manual_pause') => {
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
    setReplayState((prev) => {
      if (!prev) return prev;
      pageVisibilityReplayIdRef.current = prev.id;
      return {
        ...prev,
        status: 'pause',
        pause_reason: reason,
      };
    });
  }, []);

  const startReplay = useCallback((actions, options = {}) => {
    if (!Array.isArray(actions) || actions.length === 0 || effectiveSupportLevel < 4) {
      stopReplay('replay_not_allowed');
      return;
    }

    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }

    const nextReplayId = createReplayId();
    pageVisibilityReplayIdRef.current = null;

    setReplayState({
      id: nextReplayId,
      actions,
      source: options.source || EVENT_SOURCES.QA,
      status: 'start',
      playbackTick: 0,
      intervalMs: buildReplayIntervalMs(settings?.attentionAnimationSpeed, actions),
      loopUntil: options.loopUntil || 'until_context_change',
      questionId: options.questionId || activeQuestion?.id || null,
      guide: options.guide || null,
      startedAt: Date.now(),
    });
  }, [activeQuestion?.id, effectiveSupportLevel, settings?.attentionAnimationSpeed, stopReplay]);

  const applyLinkageActions = useCallback((rawActions, options = {}) => {
    const normalizedActions = sanitizeUiActions(rawActions, {
      graphType: resolvedGraphType,
      supportLevel: effectiveSupportLevel,
      eventSource: options.eventSource || EVENT_SOURCES.QA,
      currentQuestionId: activeQuestion?.id || null,
      currentQuestionText: activeQuestion?.text || '',
    });
    const { baseActions, replayActions } = splitReplayActions(normalizedActions, {
      supportLevel: effectiveSupportLevel,
      eventSource: options.eventSource || EVENT_SOURCES.QA,
      isCurrentQuestionContext: Boolean(options.isCurrentQuestionContext),
    });

    setTransientUiActions(baseActions);
    if (baseActions.length > 0) {
      scheduleTransientUiActionsClear();
    } else {
      clearTransientUiActions();
    }

    if (replayActions.length > 0) {
      startReplay(replayActions, {
        source: options.eventSource || EVENT_SOURCES.QA,
        questionId: activeQuestion?.id || null,
        guide: options.guide || null,
      });
    } else if (options.stopReplayIfNoReplay !== false) {
      stopReplay(options.stopReason || 'replace_replay');
    }

    // Log each action that passed sanitize
    normalizedActions.forEach((action) => {
      logAction({
        question_id: activeQuestion?.id ?? null,
        action_type: action.type,
        element_ids: action.element_ids ?? null,
        event_source: options?.eventSource ?? 'qa_linkage_event',
        base_support_level: supportLevel,
        effective_support_level: effectiveSupportLevel,
        graph_type: resolvedGraphType ?? null,
        metadata: {
          source_call: options?.logSource || 'apply_linkage',
        },
      });
    });

    return { normalizedActions, baseActions, replayActions };
  }, [
    activeQuestion?.id,
    activeQuestion?.text,
    clearTransientUiActions,
    effectiveSupportLevel,
    logAction,
    resolvedGraphType,
    scheduleTransientUiActionsClear,
    startReplay,
    stopReplay,
    supportLevel,
  ]);

  const handleChatUiActions = useCallback((actions) => {
    // A reply that belongs to an earlier task (the student switched while it was in flight)
    // must not touch this task: not its ② layer, not its chart (Codex B-R10).
    if (currentTaskGeneration !== taskGenerationRef.current) return;
    dismissInitialSuppression();
    // Auto-switch to final stage when chat AI returns element-targeting actions,
    // so that referenced elements are visible before overlays are applied.
    const ELEMENT_TARGETING_TYPES = [
      'highlight_elements',
      'focus_single_element',
      'dim_irrelevant',
      'show_value_tags',
    ];
    const hasElementTargeting = Array.isArray(actions) &&
      actions.some((a) => ELEMENT_TARGETING_TYPES.includes(a.type));

    // D5 focus can move the stage ITSELF, from inside InteractiveGraph, when the
    // student asks about an element the current stage has not revealed. That jump
    // arrives here as a bare onStageChange, which handleStageChange would read as
    // MANUAL navigation and answer with clearTransientUiActions() — deleting the
    // very focus action that caused the jump, so the target became visible but was
    // never highlighted (Codex R5 P1; the browser test missed it because it
    // asserted stage + visibility only).
    //
    // Pre-arm the programmatic window so the action survives its own jump. Unlike
    // the types above this does NOT trigger a jump here — InteractiveGraph decides
    // whether one is needed, and if it is not, the window simply expires unused.

    // D5 focus can move the stage ITSELF, from inside InteractiveGraph, when the
    // student asks about an element the current stage has not revealed. That jump
    // arrives here as a bare onStageChange, which handleStageChange would read as
    // MANUAL navigation and answer with clearTransientUiActions() — deleting the
    // very focus action that caused the jump, so the target became visible but was
    // never highlighted (Codex R5 P1; the browser test missed it because it
    // asserted stage + visibility only).
    //
    // Pre-arm the programmatic window so the action survives its own jump. Unlike
    // the types above this does NOT trigger a jump here — InteractiveGraph decides
    // whether one is needed, and if it is not, the window simply expires unused.
    const hasD5Focus = Array.isArray(actions) &&
      actions.some((a) => a?.type === 'focus_view_on_element');
    if (hasD5Focus) {
      programmaticStageJumpAtRef.current = Date.now();
    }

    if (hasElementTargeting) {
      // Mark the upcoming stage jump as programmatic so handleStageChange does
      // not immediately clear the very overlays this action group is about to apply.
      programmaticStageJumpAtRef.current = Date.now();
      // Clear first to ensure React detects a state change even if already set
      setFocusElementId(null);
      requestAnimationFrame(() => {
        setFocusElementId('__trigger_last_stage__');
        // Clean up the trigger value so it can fire again next time
        setTimeout(() => {
          setFocusElementId(null);
        }, 200);
      });
    }

    applyLinkageActions(actions, {
      eventSource: EVENT_SOURCES.QA,
      isCurrentQuestionContext: true,
      stopReason: 'next_chat_action_group',
      logSource: 'chat_qa',
    });
  }, [applyLinkageActions, currentTaskGeneration, dismissInitialSuppression, setFocusElementId]);

  // Phase 2: reset events when activeQuestion changes
  useEffect(() => {
    recentEventsRef.current = [];
    setRecentEvents([]);
    reClickMapRef.current.clear();
    recentMessagesRef.current = [];
    resetIdleTimer();
  }, [activeQuestion?.id, resetIdleTimer]);
  // A task switch restarts the idle clock too (also when neither task has a question
  // selected, which the effect above cannot see) — Codex B-R8.
  useEffect(() => {
    resetIdleTimer();
  }, [currentTaskGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2: cleanup idle timer on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      if (timers.overlay) {
        clearTimeout(timers.overlay);
      }
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
      }
      /* eslint-disable react-hooks/exhaustive-deps */
      if (burdenCloseTimerRef.current != null) clearTimeout(burdenCloseTimerRef.current);
      if (fullViewJumpRef.current.raf != null) cancelAnimationFrame(fullViewJumpRef.current.raf);
      if (fullViewJumpRef.current.timeout != null) clearTimeout(fullViewJumpRef.current.timeout);
      /* eslint-enable react-hooks/exhaustive-deps */
    };
  }, []);

  // Phase 2: handle stage change — clear transient overlays and reset idle timer.
  // Stage changes caused by our own __trigger_last_stage__ jump (within the short
  // window below) must NOT clear the overlays that triggered the jump; manual
  // stage navigation continues to clear as before.
  const PROGRAMMATIC_STAGE_JUMP_WINDOW_MS = 1000;
  const handleStageChange = useCallback((stageNum) => {
    currentStageRef.current = { stage: stageNum, generation: currentTaskGeneration };
    const isProgrammaticJump =
      Date.now() - programmaticStageJumpAtRef.current < PROGRAMMATIC_STAGE_JUMP_WINDOW_MS;
    if (isProgrammaticJump) {
      programmaticStageJumpAtRef.current = 0;
    } else {
      clearTransientUiActions();
      dismissInitialSuppression();
      stopReplay('stage_change');
    }
    resetIdleTimer();
    logAction({
      question_id: activeQuestion?.id ?? null,
      action_type: 'stage_change',
      element_ids: null,
      event_source: 'qa_linkage_event',
      base_support_level: supportLevel,
      effective_support_level: effectiveSupportLevel,
      graph_type: resolvedGraphType ?? null,
      metadata: { stage_num: stageNum, programmatic: isProgrammaticJump },
    });
  }, [activeQuestion?.id, currentTaskGeneration, clearTransientUiActions, dismissInitialSuppression, effectiveSupportLevel, logAction, resetIdleTimer, resolvedGraphType, stopReplay, supportLevel]);

  useEffect(() => {
    if (effectiveSupportLevel >= 4 || !replayState) return;
    stopReplay('support_level_dropped');
  }, [effectiveSupportLevel, replayState, stopReplay]);

  useEffect(() => {
    if (!replayState || !['start', 'continue'].includes(replayState.status)) {
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
      return undefined;
    }

    const intervalMs = buildReplayIntervalMs(settings?.attentionAnimationSpeed, replayState.actions);
    replayTimerRef.current = setInterval(() => {
      setReplayState((prev) => {
        if (!prev || !['start', 'continue'].includes(prev.status)) {
          return prev;
        }
        return {
          ...prev,
          status: 'continue',
          playbackTick: (prev.playbackTick || 0) + 1,
          intervalMs,
        };
      });
    }, intervalMs);

    return () => {
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
    };
  }, [replayState, settings?.attentionAnimationSpeed]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (replayState) {
          pauseReplay('page_hidden');
        }
        return;
      }

      if (pageVisibilityReplayIdRef.current && replayState?.id === pageVisibilityReplayIdRef.current && effectiveSupportLevel >= 4) {
        pageVisibilityReplayIdRef.current = null;
        setReplayState((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: 'continue',
            pause_reason: null,
          };
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [effectiveSupportLevel, pauseReplay, replayState]);

  const clamp = useCallback((value, min, max) => Math.min(Math.max(value, min), max), []);

  const normalizeToken = useCallback((value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase().replace(/\s+/g, '');
  }, []);

  const resolveGraphElementId = useCallback((elementId) => {
    if (!elementId) return null;
    if (graphElements.some((elem) => elem?.id === elementId)) {
      return elementId;
    }
    if (elementId.startsWith('x_axis_label_')) {
      return elementId;
    }

    if (elementId.startsWith('data_point_')) {
      const directMatch = elementId.match(/^data_point_.+_-?\d/);
      if (directMatch) {
        return elementId;
      }
      const rawValue = elementId.replace('data_point_', '');
      const normalizedTarget = normalizeToken(rawValue);
      for (const elem of graphElements) {
        const points = Array.isArray(elem?.data_points) ? elem.data_points : [];
        for (const point of points) {
          const candidates = [point?.x_label, point?.x, point?.label].filter(Boolean);
          if (!candidates.some((candidate) => normalizeToken(candidate) === normalizedTarget)) continue;
          const xRaw = point?.x ?? point?.x_value ?? point?.x_label;
          if (xRaw === null || xRaw === undefined) {
            continue;
          }
          const parsedX = Number(xRaw);
          const xToken = Number.isFinite(parsedX) ? parsedX : xRaw;
          const type = (elem?.type || '').toLowerCase();
          const isLineLike = type === 'line' || type === 'function' || type === 'function_curve';
          return isLineLike ? `data_point_${elem.id}_${xToken}` : elem.id;
        }
      }

      const byLabel = graphElements.find((elem) => normalizeToken(elem?.label) === normalizedTarget);
      if (byLabel?.id) return byLabel.id;
      return elementId;
    }

    const byLabel = graphElements.find((elem) => normalizeToken(elem?.label) === normalizeToken(elementId));
    return byLabel?.id || elementId;
  }, [graphElements, normalizeToken]);

  // A candidate may only be matched in REVERSE (keyword contains candidate) when
  // it is actually a name. `data_points[].x` is a category INDEX, so Osaka's bars
  // carry x=1 — and "1月".includes("1") was true, which lit up Osaka's JULY bar
  // when a student clicked January. Positions and bare numbers are never names.
  const isReverseMatchable = useCallback((candidate) => (
    candidate.length >= 2 && !/^\d+(\.\d+)?$/.test(candidate)
  ), []);

  const collectGraphElementMatches = useCallback((token, options = {}) => {
    const normalized = normalizeToken(token);
    if (!normalized) return [];
    const includeLabels = options.includeLabels !== false;
    const includeDataPoints = options.includeDataPoints !== false;
    const matches = [];

    graphElements.forEach((elem) => {
      if (!elem?.id) return;
      let matched = false;

      // Check element ID directly
      if (normalizeToken(elem.id) === normalized) {
        matched = true;
      }

      // Check labels
      if (!matched && includeLabels) {
        const labelCandidates = [
          elem?.label,
          elem?.name,
          elem?.seriesName,
          elem?.series_name,
          elem?.title,
          elem?.category,
        ].filter(Boolean);

        // Exact match first
        matched = labelCandidates.some((candidate) => normalizeToken(candidate) === normalized);

        // Partial match (contains) - only if token is at least 2 chars
        if (!matched && normalized.length >= 2) {
          matched = labelCandidates.some((candidate) => {
            const normalizedCandidate = normalizeToken(candidate);
            return normalizedCandidate.includes(normalized) ||
                   (isReverseMatchable(normalizedCandidate) && normalized.includes(normalizedCandidate));
          });
        }
      }

      // Check data points
      if (!matched && includeDataPoints) {
        const points = Array.isArray(elem?.data_points) ? elem.data_points : [];
        matched = points.some((point) => {
          const candidates = [
            point?.x_label,
            point?.x,
            point?.x_value,
            point?.label,
            point?.category,
            point?.name,
          ].filter(Boolean);
          return candidates.some((candidate) => {
            const normalizedCandidate = normalizeToken(candidate);
            return normalizedCandidate === normalized ||
                   (normalized.length >= 2 && (
                     normalizedCandidate.includes(normalized) ||
                     (isReverseMatchable(normalizedCandidate) && normalized.includes(normalizedCandidate))
                   ));
          });
        });
      }

      if (matched) matches.push(elem.id);
    });

    return matches;
  }, [graphElements, normalizeToken, isReverseMatchable]);

  const resolveGraphElementTargets = useCallback((elementId, keywordText) => {
    const highlightIds = [];
    const pushUnique = (id) => {
      if (!id || highlightIds.includes(id)) return;
      highlightIds.push(id);
    };

    if (elementId) {
      if (elementId.startsWith('x_axis_label_')) {
        const rawLabel = elementId.replace('x_axis_label_', '');
        const dataMatches = collectGraphElementMatches(rawLabel, { includeLabels: false, includeDataPoints: true });
        dataMatches.forEach(pushUnique);
        if (!dataMatches.length) {
          const labelMatches = collectGraphElementMatches(rawLabel, { includeLabels: true, includeDataPoints: false });
          labelMatches.forEach(pushUnique);
        }
        const axisId = resolveGraphElementId(elementId);
        if (axisId) {
          pushUnique(axisId);
        }
      } else {
        const resolvedId = resolveGraphElementId(elementId);
        if (resolvedId) {
          pushUnique(resolvedId);
        }
      }
    }

    if (!highlightIds.length && keywordText) {
      const fallbackMatches = collectGraphElementMatches(keywordText, { includeLabels: true, includeDataPoints: true });
      fallbackMatches.forEach(pushUnique);
    }

    return {
      focusId: highlightIds[0] || null,
      highlightIds,
    };
  }, [collectGraphElementMatches, resolveGraphElementId]);
  // Every callback ref of this view is published in the commit phase, never during render:
  // a render React abandons (Suspense / transition) must not repoint the visible task's
  // handlers at the next task's closures (Codex B-R6). Declared after the last of them.
  useLayoutEffect(() => {
    logBehaviourEventRef.current = logBehaviourEvent;
    dismissInitialSuppressionRef.current = dismissInitialSuppression;
    questionStartRef.current = questionStart;
    sendQuestionStartRef.current = sendQuestionStart;
    activeQuestionRef.current = activeQuestion;
    resolveTargetsRef.current = resolveGraphElementTargets;
  });

  const getContainerWidth = useCallback(() => {
    return layoutInfoRef.current.containerWidth || containerRef.current?.clientWidth || (graphWidthRef.current + minSideWidth);
  }, [minSideWidth]);

  const getMaxGraphWidth = useCallback(() => {
    const containerWidth = getContainerWidth();
    const gap = layoutInfoRef.current.gap ?? 16;
    const separatorWidth = layoutInfoRef.current.separatorWidth ?? 16;
    const reservedWidth = minSideWidth + separatorWidth + gap * 2;
    return Math.max(containerWidth - reservedWidth, minGraphWidth);
  }, [getContainerWidth, minGraphWidth, minSideWidth]);

  const resetGraphWidth = useCallback(() => {
    const maxGraphWidth = getMaxGraphWidth();
    const containerWidth = getContainerWidth();
    const defaultWidth = clamp(containerWidth * defaultGraphRatio, minGraphWidth, maxGraphWidth);
    graphWidthRef.current = defaultWidth;
    setGraphPanelWidth(defaultWidth);
  }, [clamp, defaultGraphRatio, getContainerWidth, getMaxGraphWidth, minGraphWidth]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      stopAudio();
      if (timers.graph) clearTimeout(timers.graph);
      if (timers.text) clearTimeout(timers.text);
      if (timers.tts) clearTimeout(timers.tts);
    };
  }, [stopAudio]);

  useEffect(() => {
    graphWidthRef.current = graphPanelWidth;
  }, [graphPanelWidth]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.contentRect.width;
      const previous = layoutInfoRef.current.containerWidth;
      layoutInfoRef.current.containerWidth = width;
      if (element) {
        const styles = window.getComputedStyle(element);
        const columnGap = parseFloat(styles.columnGap || styles.gap || '0');
        if (!Number.isNaN(columnGap)) {
          layoutInfoRef.current.gap = columnGap;
        }
      }
      if (separatorRef.current) {
        layoutInfoRef.current.separatorWidth = separatorRef.current.offsetWidth;
      }

      const maxGraphWidth = getMaxGraphWidth();
      let nextWidth = graphWidthRef.current;

      if (!previous) {
        nextWidth = clamp(width * defaultGraphRatio, minGraphWidth, maxGraphWidth);
      } else {
        nextWidth = clamp(nextWidth, minGraphWidth, maxGraphWidth);
      }

      if (Math.abs(nextWidth - graphWidthRef.current) > 1) {
        setGraphPanelWidth(nextWidth);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [clamp, defaultGraphRatio, getMaxGraphWidth, minGraphWidth]);

  const getRelativeRect = useCallback((domRect) => {
    if (!domRect || !containerRef.current) return null;
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      x: domRect.left - containerRect.left,
      y: domRect.top - containerRect.top,
      width: domRect.width,
      height: domRect.height,
    };
  }, []);

  const resolveGraphElementRect = useCallback((elementId) => {
    if (!containerRef.current || !elementId) return null;
    const node = containerRef.current.querySelector(`[data-graph-element-id="${CSS.escape(elementId)}"]`);
    if (!node?.getBoundingClientRect) return null;
    return getRelativeRect(node.getBoundingClientRect());
  }, [getRelativeRect]);

  // questionId: the question the speech is about (passed by the caller when the selection
  // has not reached state yet); otherwise the current one. The events of one request keep
  // that question and its task; a result after a task switch is dropped (Codex B-R7).
  const speakText = useCallback(async (text, trigger = 'other', questionId) => {
    if (!text || settings?.voiceOutputEnabled === false) return;
    stopAudio();
    const origin = {
      generation: taskGenerationRef.current,
      questionId: questionId !== undefined ? questionId : researchCtxRef.current.questionId,
    };
    const logTts = (kind) => {
      if (kind !== 'tts_start' && origin.generation !== taskGenerationRef.current) return;
      logResearchAction(kind, { questionId: origin.questionId, metadata: { trigger } });
    };
    logTts('tts_start');
    try {
      const response = await ttsAPI.synthesize({
        text,
        voice_type: settings?.voiceType || 'female_neural',
        speaking_rate: settings?.speechRate || 1.0,
        volume_gain_db: 0.0,
      });
      const audioBase64 = response.data?.audio_content || response.data?.audioContent;
      const audioUrl = createAudioUrlFromBase64(audioBase64);
      if (!audioUrl) {
        logTts('tts_error');
        return;
      }
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.addEventListener('ended', () => {
        try {
          URL.revokeObjectURL(audioUrl);
        } catch (e) {
          // ignore
        }
      }, { once: true });
      await audio.play();
      logTts('tts_success');
    } catch (err) {
      logTts('tts_error');
      logClientWarn('TTS failed:', err);
    }
  }, [logResearchAction, settings?.speechRate, settings?.voiceOutputEnabled, settings?.voiceType, stopAudio]);

  const clearGraphHighlightsLater = useCallback(() => {
    if (timersRef.current.graph) clearTimeout(timersRef.current.graph);
    timersRef.current.graph = setTimeout(() => {
      setHighlightedGraphElements([]);
      setFocusElementId(null);
      setOneShotAttentionGuide(null);
    }, highlightDuration);
  }, [highlightDuration, setFocusElementId]);

  const clearTextHighlightsLater = useCallback(() => {
    if (timersRef.current.text) clearTimeout(timersRef.current.text);
    timersRef.current.text = setTimeout(() => setHighlightedTextKeywords([]), highlightDuration);
  }, [highlightDuration]);

  const handleKeywordClick = useCallback((keyword, domRect, context = null) => {
    if (!keyword?.text) return;
    dismissInitialSuppression();

    logAction({
      question_id: activeQuestion?.id || null,
      action_type: 'keyword_click',
      element_ids: null,
      event_source: 'click_event',
      base_support_level: supportLevel,
      effective_support_level: effectiveSupportLevel,
      graph_type: resolvedGraphType || null,
    });

    stopReplay('keyword_linkage_replace');
    const contextQuestion = context?.question;
    if (contextQuestion?.id && contextQuestion.id !== activeQuestion?.id) {
      setActiveQuestion(contextQuestion);
    }
    setHighlightedTextKeywords([keyword.text]);
    clearTextHighlightsLater();

    // 查找映射：优先使用 integrationHints，然后使用关键词自带的 graph_element_id
    const preferredQuestionId = contextQuestion?.id || activeQuestion?.id;
    const preferred = preferredQuestionId
      ? integrationHints.find((h) => h?.text_keyword === keyword.text && h?.question_id === preferredQuestionId)
      : null;
    const mapping = preferred || integrationHints.find((h) => h?.text_keyword === keyword.text);

    // 使用映射的 graph_element_id，或者关键词自带的 graph_element_id
    const graphElementId = mapping?.graph_element_id || keyword?.graph_element_id || null;

    const { focusId, highlightIds } = resolveGraphElementTargets(graphElementId, keyword.text);

    // 即使没有找到精确匹配，也尝试跳转到最后阶段并显示高亮
    // 这样至少可以让用户看到完整图表
    if (!focusId && (!highlightIds || highlightIds.length === 0)) {
      // 设置一个虚拟的 focusElementId 来触发跳转到最后阶段
      setHighlightedGraphElements([]);
      setFocusElementId('__trigger_last_stage__');
      setOneShotAttentionGuide(null);
      return;
    }

    const nextHighlights = highlightIds && highlightIds.length ? highlightIds : (focusId ? [focusId] : []);
    setHighlightedGraphElements(nextHighlights);
    setFocusElementId(focusId);
    clearGraphHighlightsLater();

    const dimIds = graphElements
      .map((elem) => elem?.id)
      .filter((id) => id && !nextHighlights.includes(id));
    const qaActions = [];
    if (nextHighlights.length > 1) {
      // Multi-element match: highlight ALL targets. focus_single_element would
      // dim every element except the first match, leaving only one visible
      // (the "3 elements flash, then only 1 keeps pulsing" bug).
      qaActions.push({ type: 'highlight_elements', element_ids: nextHighlights });
    } else if (focusId) {
      qaActions.push({ type: 'focus_single_element', element_ids: [focusId] });
    } else if (nextHighlights.length > 0) {
      qaActions.push({ type: 'highlight_elements', element_ids: nextHighlights });
    }
    if (effectiveSupportLevel >= 2 && dimIds.length > 0) {
      qaActions.push({ type: 'dim_irrelevant', element_ids: dimIds });
    }

    const from = getRelativeRect(domRect);
    const to = resolveGraphElementRect(focusId || nextHighlights[0]);
    const guide = (from && to)
      ? {
          from,
          to,
          message: mapping?.hint_message || t('integration.lookHere'),
        }
      : null;

    const speechText = mapping?.hint_message || keyword.text;
    if (speechText) {
      speakText(speechText, 'keyword_click');
    }

    if (guide) {
      setOneShotAttentionGuide(guide);
    } else {
      setOneShotAttentionGuide(null);
    }

    applyLinkageActions(qaActions, {
      eventSource: EVENT_SOURCES.QA,
      isCurrentQuestionContext: true,
      guide: effectiveSupportLevel >= 4 ? guide : null,
      stopReason: 'keyword_linkage_refresh',
      logSource: 'keyword_linkage',
    });
  }, [
    setActiveQuestion,
    activeQuestion?.id,
    applyLinkageActions,
    clearGraphHighlightsLater,
    clearTextHighlightsLater,
    dismissInitialSuppression,
    effectiveSupportLevel,
    getRelativeRect,
    graphElements,
    integrationHints,
    logAction,
    resolvedGraphType,
    resolveGraphElementTargets,
    resolveGraphElementRect,
    setFocusElementId,
    speakText,
    stopReplay,
    supportLevel,
    t,
  ]);

  const handleGraphElementClick = useCallback((elementId) => {
    if (!elementId) return;
    dismissInitialSuppression();

    logAction({
      question_id: activeQuestion?.id || null,
      action_type: 'graph_element_click',
      element_ids: [elementId],
      event_source: 'click_event',
      base_support_level: supportLevel,
      effective_support_level: effectiveSupportLevel,
      graph_type: resolvedGraphType || null,
    });

    stopReplay('graph_click_replace');
    // Phase 2: track re-clicks for event collection
    trackReClick(elementId);

    // ★ IMPORTANT: Also highlight the clicked graph element itself
    setHighlightedGraphElements([elementId]);
    clearGraphHighlightsLater();

    // 1. Look up in integration_hints by element ID
    let relatedHints = integrationHints.filter((h) => h?.graph_element_id === elementId);

    // 2. Handle data_point_ prefix - try parent element
    if (!relatedHints.length && elementId.startsWith('data_point_')) {
      const rawId = elementId.replace(/^data_point_/, '');
      const lastUnderscore = rawId.lastIndexOf('_');
      const parentId = lastUnderscore > 0 ? rawId.slice(0, lastUnderscore) : rawId;
      if (parentId) {
        relatedHints = integrationHints.filter((h) => h?.graph_element_id === parentId);
      }
    }

    // 3. Extract element labels for fallback matching
    const targetElement = graphElements.find((elem) => elem?.id === elementId);
    const elementLabels = [];

    if (targetElement) {
      [targetElement.label, targetElement.name, targetElement.seriesName].forEach((value) => {
        if (value && typeof value === 'string') elementLabels.push(value.trim());
      });
      const points = Array.isArray(targetElement?.data_points) ? targetElement.data_points : [];
      points.forEach((point) => {
        [point?.x_label, point?.x, point?.label].forEach((value) => {
          if (value && typeof value === 'string') elementLabels.push(value.trim());
        });
      });
    }

    // If no element found by ID, try to extract label from the elementId itself
    // For bar charts, elementId might be like "bar_0" but legend shows "cats - Justin"
    if (!targetElement && elementId) {
      // Try to find element by matching ID pattern
      const barMatch = elementId.match(/^bar_(\d+)$/);
      if (barMatch) {
        const barIndex = parseInt(barMatch[1], 10);
        const barElements = graphElements.filter(e => e?.type === 'bar');
        if (barElements[barIndex]) {
          const barElem = barElements[barIndex];
          [barElem.label, barElem.name, barElem.seriesName].forEach((value) => {
            if (value && typeof value === 'string') elementLabels.push(value.trim());
          });
          const points = Array.isArray(barElem?.data_points) ? barElem.data_points : [];
          points.forEach((point) => {
            [point?.x_label, point?.x, point?.label].forEach((value) => {
              if (value && typeof value === 'string') elementLabels.push(value.trim());
            });
          });
        }
      }
    }

    // Expand labels: split compound labels like "rabbits - Justin" into separate parts
    const expandedLabels = [...elementLabels];
    elementLabels.forEach((label) => {
      // Split by common separators: " - ", "-", "_", "：", ":"
      const parts = label.split(/\s*[-_：:]\s*/);
      parts.forEach((part) => {
        const trimmed = part.trim();
        if (trimmed.length >= 2 && !expandedLabels.includes(trimmed)) {
          expandedLabels.push(trimmed);
        }
      });
    });

    // 4. Try matching expanded labels against integration hints
    if (!relatedHints.length && expandedLabels.length) {
      const tokens = new Set(expandedLabels.map(normalizeToken).filter(Boolean));
      relatedHints = integrationHints.filter((h) => tokens.has(normalizeToken(h?.text_keyword)));
    }

    let related = relatedHints.map((h) => h?.text_keyword).filter(Boolean);

    // 5. Match against auto-generated keywords (stem + question keywords)
    // Always run (not just when related is empty) to find all matching keywords
    const allAutoKeywords = [...enhancedStemKeywords];
    // Also collect auto-detected keywords from enhanced questions
    (enhancedQuestions || []).forEach((q) => {
      (q?.keywords || []).forEach((kw) => {
        if (kw?.type === 'auto_detected' && kw?.text) {
          allAutoKeywords.push(kw);
        }
      });
    });

    if (allAutoKeywords.length) {
      allAutoKeywords.forEach((kw) => {
        // Check if keyword is directly linked to this element
        if (kw?.graph_element_id === elementId) {
          if (!related.includes(kw.text)) {
            related.push(kw.text);
          }
        }
        // Also check if keyword text matches any expanded label (case-insensitive)
        const kwText = kw?.text || '';
        if (kwText && expandedLabels.some(label => normalizeToken(label) === normalizeToken(kwText))) {
          if (!related.includes(kwText)) {
            related.push(kwText);
          }
        }
      });
    }

    // 6. Always search expanded labels in problem text (case-insensitive)
    // Use label directly (ProblemTextPanel now does case-insensitive matching)
    if (expandedLabels.length) {
      const stemText = problemData?.stem || '';
      const allQuestionTexts = (problemData?.questions || []).map((q) => q?.text || '').join(' ');
      const combinedText = `${stemText} ${allQuestionTexts}`;
      const lowerCombined = combinedText.toLowerCase();

      expandedLabels.forEach((label) => {
        if (label.length >= 2) {
          const lowerLabel = label.toLowerCase();
          if (lowerCombined.includes(lowerLabel)) {
            // Push the label directly - ProblemTextPanel uses case-insensitive matching
            const alreadyIncluded = related.some((r) => r.toLowerCase() === lowerLabel);
            if (!alreadyIncluded) {
              related.push(label);
            }
          }
        }
      });
    }

    if (!related.length) return;

    // 7. Auto-switch to related question if applicable
    const questionIds = Array.from(new Set(relatedHints.map((h) => h?.question_id).filter(Boolean)));
    if (questionIds.length === 1) {
      const targetQuestion = questionsById.get(questionIds[0]);
      if (targetQuestion && targetQuestion.id !== activeQuestion?.id) {
        setActiveQuestion(targetQuestion);
      }
    }

    setHighlightedTextKeywords(related);
    clearTextHighlightsLater();

    const clickActions = [{ type: 'focus_single_element', element_ids: [elementId] }];
    const dimIds = graphElements
      .map((elem) => elem?.id)
      .filter((id) => id && id !== elementId);
    if (effectiveSupportLevel >= 2 && dimIds.length > 0) {
      clickActions.push({ type: 'dim_irrelevant', element_ids: dimIds });
    }

    applyLinkageActions(clickActions, {
      eventSource: EVENT_SOURCES.CLICK,
      isCurrentQuestionContext: Boolean(activeQuestion?.id && questionIds.includes(activeQuestion.id)),
      stopReason: 'graph_click_context_switch',
      logSource: 'graph_click_linkage',
    });
  }, [
    setActiveQuestion,
    activeQuestion?.id,
    applyLinkageActions,
    clearGraphHighlightsLater,
    clearTextHighlightsLater,
    dismissInitialSuppression,
    effectiveSupportLevel,
    enhancedQuestions,
    enhancedStemKeywords,
    graphElements,
    integrationHints,
    logAction,
    normalizeToken,
    problemData?.questions,
    problemData?.stem,
    questionsById,
    resolvedGraphType,
    stopReplay,
    supportLevel,
    trackReClick
  ]);

  const handleQuestionSelect = useCallback((question) => {
    const firstStart = Boolean(question?.id) && !questionFirstStartRef.current.has(question.id);
    // Same question again: the id does not change, so the activeQuestion effect will
    // not run — record the re-presentation here (Codex R1 P2).
    if (question?.id && question.id === activeQuestion?.id) {
      questionStartRef.current?.(question);
    }
    setActiveQuestion(question);
    setHighlightedTextKeywords([]);
    setHighlightedGraphElements([]);
    setFocusElementId(null);
    setOneShotAttentionGuide(null);
    clearTransientUiActions();
    dismissInitialSuppression();
    stopReplay('question_selected');
    // Phase A ②: a question's first presentation gets the initial display too.
    if (firstStart) applyInitialSuppression(question, 'question_start');
    if (question?.text) {
      speakText(question.text, 'question_select', question.id);
    }
  }, [activeQuestion?.id, applyInitialSuppression, clearTransientUiActions, dismissInitialSuppression, setActiveQuestion, setFocusElementId, speakText, stopReplay]);

  const handleMessageSent = useCallback((messageText) => {
    clearTransientUiActions();
    dismissInitialSuppression();
    stopReplay('next_user_message');
    trackReQuestion(messageText);
  }, [clearTransientUiActions, dismissInitialSuppression, stopReplay, trackReQuestion]);

  const activeAttentionGuide = replayState?.guide
    ? {
        ...replayState.guide,
        animationKey: `${replayState.id}:${replayState.playbackTick || 0}`,
        paused: replayState.status === 'pause',
      }
    : oneShotAttentionGuide;

  return (
    <div className="relative rounded-[28px] border p-4 backdrop-blur min-h-[70vh]" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', boxShadow: 'var(--shadow-lg)' }}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="text-2xl">🧩</div>
          <div>
            <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}><FuriganaText text={t('integration.title')} /></div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('integration.subtitle')} /></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-muted)' }}>
            <FuriganaText text={researchFeatures.ui_support_words === true
              ? t('integration.supportWordsChosen', { name: t(`chat.supportWords.levels.${supportLevel}.name`) })
              : t('integration.supportLevel', { level: supportLevel })} />
          </span>
          {effectiveSupportLevel !== supportLevel && (
            <span className="rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: 'var(--primary)', backgroundColor: 'var(--primary-bg)', color: 'var(--primary)' }}>
              <FuriganaText text={researchFeatures.ui_support_words === true
                ? t('integration.supportWordsNow', { name: t(`chat.supportWords.levels.${effectiveSupportLevel}.name`) })
                : t('integration.effectiveLevel', { level: effectiveSupportLevel })} />
            </span>
          )}
          {onExit && (
            <button
              onClick={onExit}
              className="rounded-full border px-4 py-2 text-sm font-semibold"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', color: 'var(--text)' }}
            >
              ← <FuriganaText text={t('integration.backToDashboard')} />
            </button>
          )}
        </div>
      </div>

      <div ref={containerRef} className="relative">
        <div ref={rowRef} className={`flex min-h-0 gap-4 ${twoColumn ? 'flex-row items-stretch' : 'flex-col'}`}>
          <div
            style={{
              width: graphPanelWidth,
              minWidth: minGraphWidth,
              maxWidth: '100%',
              // No width transition: while it animated, the chart's ResizeObserver saw a
              // new fractional width every frame and redrew the whole chart each time
              // (12–39 redraws at startup; e2e/tier1-a11y/redraw-budget.spec.js).
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-surface)',
              boxShadow: 'var(--shadow-sm)'
            }}
            className={`flex-shrink-0 flex min-h-0 flex-col rounded-3xl border p-3 ${twoColumn ? 'sticky top-2 self-start' : ''}`}
          >
            <InteractiveGraph
              onRenderStateChange={handleRenderStateChange}
              graphData={resolvedGraphData}
              studentProfile={studentProfile}
              settings={settings}
              sessionId={sessionId}
              highlightedElements={highlightedGraphElements}
              focusElementId={focusElementId}
              supportLevel={effectiveSupportLevel}
              onElementClick={handleGraphElementClick}
              uiActions={mergedUiActions}
              replayState={replayState}
              onStageChange={handleStageChange}
              activeQuestionId={activeQuestion?.id || null}
              onElementHoverDwell={instrumented ? handleElementHoverDwell : null}
              onTtsEvent={instrumented ? handleTtsEvent : null}
            />
          </div>

          <div
            role="separator"
            aria-orientation="vertical"
            className="relative hidden w-4 cursor-col-resize rounded-full transition-all lg:flex"
            style={{ backgroundColor: 'var(--border)' }}
            ref={separatorRef}
            onPointerDown={(event) => {
              event.preventDefault();
              const startX = event.clientX;
              const initialWidth = graphWidthRef.current;
              const maxGraphWidth = getMaxGraphWidth();

              const handleMove = (e) => {
                const delta = e.clientX - startX;
                const nextWidth = clamp(initialWidth + delta, minGraphWidth, maxGraphWidth);
                graphWidthRef.current = nextWidth;
                setGraphPanelWidth(nextWidth);
              };

              const handleUp = () => {
                window.removeEventListener('pointermove', handleMove);
              };

              window.addEventListener('pointermove', handleMove);
              window.addEventListener('pointerup', handleUp, { once: true });
              window.addEventListener('pointercancel', handleUp, { once: true });
            }}
            onDoubleClick={resetGraphWidth}
          >
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1" style={{ color: 'var(--text-muted)' }}>
              <span className="text-lg leading-none">⋮</span>
              <span className="text-xs leading-none">⋮</span>
            </div>
          </div>

          <div
            style={{ flex: 1, minWidth: minSideWidth }}
            className="flex min-h-0 flex-col gap-4"
          >
            <ProblemTextPanel
              stem={problemData?.stem}
              stemKeywords={enhancedStemKeywords}
              questions={enhancedQuestions}
              activeQuestion={activeQuestion}
              highlightedKeywords={highlightedTextKeywords}
              settings={settings}
              onQuestionSelect={handleQuestionSelect}
              onKeywordClick={handleKeywordClick}
              onAnswerSubmit={instrumented ? handleAnswerSubmit : null}
              linkInvitationVisible={instrumented && linkInvitationVisible}
              onLinkInvitationDismiss={handleLinkInvitationDismiss}
              onPanelRevisit={instrumented ? handlePanelRevisit : null}
              revisitScope={currentTaskGeneration}
            />

            {instrumented && researchFeatures.burden_probe && activeBurdenProbe && (
              <BurdenProbe key={`burden-${activeBurdenProbe.promptId}`} onAnswer={handleBurdenAnswer} />
            )}

            <div className="flex-1 min-h-0 rounded-3xl border overflow-hidden" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}>
                <ChatInterface
                  studentProfile={studentProfile}
                  graphId={graphId || resolvedGraphData?.graph_id}
                  settings={settings}
                  sessionId={sessionId}
                  graphType={graphData?.graph_type}
                  supportLevel={supportLevel}
                  effectiveSupportLevel={effectiveSupportLevel}
                onSupportLevelChange={handleSupportLevelChange}
                supportWords={researchFeatures.ui_support_words === true}
                problemId={problemId}
                questionId={activeQuestion?.id || null}
                onUiActions={handleChatUiActions}
                recentEvents={recentEvents}
                onMessageSent={handleMessageSent}
                renderState={renderState}
                onTtsEvent={instrumented ? handleTtsEvent : null}
              />
            </div>
          </div>
        </div>

        {activeAttentionGuide && (
          <AttentionGuide
            from={activeAttentionGuide.from}
            to={activeAttentionGuide.to}
            message={activeAttentionGuide.message}
            animationSpeed={settings?.attentionAnimationSpeed ?? 1}
            animationKey={activeAttentionGuide.animationKey}
            paused={activeAttentionGuide.paused}
          />
        )}
      </div>
    </div>
  );
}

export default ProblemIntegrationView;
